import React, { forwardRef, useImperativeHandle } from 'react';
import { Controller } from 'react-hook-form';
import { useTheme } from '../../../hooks/useTheme';
import { useCartStore } from '../../../stores/cartStore';
import { usePrintForm } from '../../../hooks/usePrintForm';
import { useEffect, useState } from 'react';
import { TechnologyField } from './Fields/TechnologyField';
import { PrinterField } from './Fields/PrinterField';
import { MaterialField } from './Fields/MaterialField';
import { ColorField } from './Fields/ColorField';
import { InfillField } from './Fields/InfillField';
import { QualityField } from './Fields/QualityField';
import { AdditionalServicesField } from './Fields/AdditionalServicesField';
import { PrintServices } from '../../../types/cart';
import { FormValues } from '../../../themes/base/tokens';

interface PrintFormProps {
  volumeCm3: number;
  onCalculatePrice: (price: number) => void;
  onValidChange: (isValid: boolean) => void;
  cardIndex: number;
}

export interface PrintFormRef {
  prepareServices: () => PrintServices | null;
}

export const PrintForm = forwardRef<PrintFormRef, PrintFormProps>(
  ({ volumeCm3, onCalculatePrice, onValidChange, cardIndex }, ref) => {
    const cartStore = useCartStore();
    const theme = useTheme(state => state.getThemeStyles());
    const [isFormValid, setIsFormValid] = useState(false);
    const [activeField, setActiveField] = useState<string | undefined>(undefined);

    const {
      control,
      formValues,
      selectedData,
      handlers,
      isFieldActive,
      prepareServices
    } = usePrintForm(volumeCm3, onCalculatePrice);

    const handleFieldFocus = (fieldName: string) => {
      setActiveField(fieldName);
    };

    const handleFieldBlur = () => {
      setActiveField(undefined);
    };

    const isFieldEnabled = (fieldName: string): boolean => {
      const values = formValues;
      switch (fieldName) {
        case 'technology':
          return true;
        case 'printer':
          return !!values.technology;
        case 'material':
          return !!values.technology && !!values.printer;
        case 'color':
          return !!values.technology && !!values.printer && !!values.material;
        case 'infill':
          return !!values.technology && !!values.printer && !!values.material && !!values.color;
        case 'quality':
          return !!values.technology && !!values.printer && !!values.material && !!values.color && !!values.infill;
        case 'additionalServices':
          return !!values.technology && !!values.printer && !!values.material && !!values.color && !!values.infill && !!values.quality;
        default:
          return false;
      }
    };

    const getFieldState = (fieldName: keyof FormValues) => {
      const fieldOrder: Array<keyof FormValues> = ['technology', 'printer', 'material', 'color', 'infill', 'quality'];
      const currentIndex = fieldOrder.findIndex(field => !formValues[field]);
      const fieldIndex = fieldOrder.indexOf(fieldName);

      if (currentIndex === -1) return 'default';
      if (fieldIndex === currentIndex) return 'active';
      if (fieldIndex > currentIndex && isFieldEnabled(fieldName)) return 'next';
      return 'default';
    };

    useEffect(() => {
      const isValid = !!formValues.technology && 
                      !!formValues.printer && 
                      !!formValues.material && 
                      !!formValues.color && 
                      !!formValues.infill && 
                      !!formValues.quality;
      
      setIsFormValid(isValid);
      onValidChange(isValid);
    }, [formValues, onValidChange]);

    useImperativeHandle(ref, () => ({
      prepareServices
    }));

    return (
      <div className={theme.components.form.layout.wrapper}>
        <div className="h-1" />
        <div className={theme.components.form.layout.content.grid}>
          <Controller
            name="technology"
            control={control}
            render={({ field }) => (
              <TechnologyField
                value={field.value}
                onChange={handlers.handleTechnologyChange}
                isActive={isFieldActive('technology')}
                isNext={getFieldState('technology') === 'next'}
                disabled={!isFieldEnabled('technology')}
              />
            )}
          />
          <Controller
            name="printer"
            control={control}
            render={({ field }) => (
              <PrinterField
                value={field.value}
                onChange={handlers.handlePrinterChange}
                selectedTechnology={formValues.technology}
                isActive={isFieldActive('printer')}
                isNext={getFieldState('printer') === 'next'}
                disabled={!isFieldEnabled('printer')}
              />
            )}
          />
          <Controller
            name="material"
            control={control}
            render={({ field }) => (
              <MaterialField
                value={field.value}
                onChange={handlers.handleMaterialChange}
                selectedTechnology={formValues.technology}
                selectedPrinter={formValues.printer}
                isActive={isFieldActive('material')}
                isNext={getFieldState('material') === 'next'}
                disabled={!isFieldEnabled('material')}
              />
            )}
          />
          <Controller
            name="color"
            control={control}
            render={({ field }) => (
              <ColorField
                value={field.value}
                onChange={handlers.handleColorChange}
                selectedTechnology={formValues.technology}
                selectedPrinter={formValues.printer}
                selectedMaterial={formValues.material}
                isActive={getFieldState('color') === 'active'}
                isNext={getFieldState('color') === 'next'}
                disabled={!isFieldEnabled('color')}
              />
            )}
          />
          <Controller
            name="infill"
            control={control}
            render={({ field }) => (
              <InfillField
                value={field.value}
                onChange={handlers.handleInfillChange}
                selectedTechnology={formValues.technology}
                selectedPrinter={formValues.printer}
                selectedMaterial={formValues.material}
                selectedColor={formValues.color}
                isActive={getFieldState('infill') === 'active'}
                isNext={getFieldState('infill') === 'next'}
                disabled={!isFieldEnabled('infill')}
              />
            )}
          />
          <Controller
            name="quality"
            control={control}
            render={({ field }) => (
              <QualityField
                value={field.value}
                onChange={handlers.handleQualityChange}
                selectedTechnology={formValues.technology}
                selectedPrinter={formValues.printer}
                selectedMaterial={formValues.material}
                selectedColor={formValues.color}
                selectedInfill={formValues.infill}
                isActive={getFieldState('quality') === 'active'}
                isNext={getFieldState('quality') === 'next'}
                disabled={!isFieldEnabled('quality')}
              />
            )}
          />
          <div className={theme.components.form.group.fullWidth}>
            <Controller
              name="additionalServices"
              control={control}
              render={({ field }) => (
                <AdditionalServicesField
                  value={field.value}
                  onChange={handlers.handleAdditionalServicesChange}
                  selectedTechnology={formValues.technology}
                  selectedPrinter={formValues.printer}
                  selectedMaterial={formValues.material}
                  selectedColor={formValues.color}
                  selectedInfill={formValues.infill}
                  selectedQuality={formValues.quality}
                  disabled={!isFieldEnabled('additionalServices')}
                />
              )}
            />
          </div>
        </div>
      </div>
    );
  }
);